import React, { useContext, useState } from "react";
import { Tooltip, IconButton } from "@mui/material";
import { useSnackbar } from "notistack";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationDialog from "./ConfirmationDialogComponent";
import { context, ContextData } from "utils/Provider";
import { 
  DatasetsByCubeIdDocument, 
  DatasetPreviewDocument 
} from "graphql/types/graphql";

type MutationOptions = {
  variables: any;
  refetchQueries?: any[];
  onCompleted?: () => void;
  onError?: (error: Error) => void;
};

type MutationVariables = 
  | { cubeName: string; datasetId: string }
  | { cubeId: string; viewName: string };

type DatasetRefetchQueries = [
  {
    query: typeof DatasetsByCubeIdDocument;
    variables: {
      cubeId: string;
    };
  },
  {
    query: typeof DatasetPreviewDocument;
    variables: {
      filter: {
        cubeName: string;
        datasetId: string;
      };
    };
  }
];

interface DeleteButtonProps {
  itemId: string;
  onDelete?: () => void;
  itemName: string;
  confirmationMessage?: string;
  disableDeleteButton?: boolean;
  mutationFn: (options: MutationOptions) => Promise<any>;
  mutationLoading: boolean;
  getMutationVariables: (itemId: string, contextData: ContextData) => MutationVariables;
  getRefetchQueries?: (itemId: string, contextData: ContextData) => DatasetRefetchQueries;
  successMessage?: string;
  deleteTooltip?: string;
}

const GenericDeleteButton: React.FC<DeleteButtonProps> = ({
  itemId,
  onDelete,
  itemName,
  confirmationMessage = "Are you sure you want to delete this item?",
  disableDeleteButton = false,
  mutationFn,
  mutationLoading,
  getMutationVariables,
  getRefetchQueries,
  successMessage = "Item deleted successfully!",
  deleteTooltip = "Delete",
}) => {
  const contextData = useContext(context);
  const { setIsLoading } = contextData;
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleConfirmDelete = () => {
    setIsLoading(true);
    
    const mutationOptions: MutationOptions = {
      variables: getMutationVariables(itemId, contextData),
      onCompleted: () => {
        enqueueSnackbar(successMessage, { variant: "success" });
        if (onDelete) onDelete();
      },
      onError: (error: Error) => {
        enqueueSnackbar(`Delete error: ${error.message}`, { variant: "error" });
      }
    };
    
    if (getRefetchQueries) {
      mutationOptions.refetchQueries = getRefetchQueries(itemId, contextData);
    }
    
    mutationFn(mutationOptions)
      .catch((error: Error) => {
        enqueueSnackbar(`Delete error: ${error.message}`, { variant: "error" });
      })
      .finally(() => {
        setIsLoading(false);
        handleCloseDialog();
      });
  };
  
  return (
    <>
      <Tooltip title={deleteTooltip}>
        <span>
          <IconButton
            color="error"
            size="small"
            onClick={handleOpenDialog}
            disabled={mutationLoading || !itemId || disableDeleteButton}
            aria-label="delete data"
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmationDialog
        open={dialogOpen}
        onCancel={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title={itemName}
        message={confirmationMessage}
      />
    </>
  );
};

export default GenericDeleteButton;